/**
 * Role Utility for Landscape
 * Provides consistent labeling, styling, and icons for all user roles across the platform.
 */
import { cn } from './cn';

export interface RoleConfig {
  label: string;
  variant: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'purple' | 'orange' | 'pink' | 'secondary';
  icon: string;
}

export const ROLE_MAP: Record<string, string> = {
  'super_admin': 'Quản trị viên tối cao',
  'admin': 'Quản trị viên',
  'director': 'Giám đốc',
  'pm': 'Quản lý dự án',
  'qa_qc': 'QA/QC',
  'qs': 'QS',
  'site_manager': 'Chỉ huy trưởng',
  'warehouse': 'Thủ kho',
  'accountant': 'Kế toán',
  'secretary': 'Thư ký dự án',
  'supervisor': 'Giám sát ',
  'staff': 'Nhân viên ',
  'site_foreman': 'Tổ trưởng',
  'equipment_manager': 'Quản lý thiết bị',
  'zone_manager': 'Quản lý Khu vực',
  'agro_manager': 'Quản lý Nông hóa phẩm'
};

/**
 * roleConfigMap - Standardized configuration for all roles
 * Follows the same pattern as StatusConfig for UI consistency.
 */
export const roleConfigMap: Record<string, RoleConfig> = {
  super_admin: { label: 'Quản trị viên tối cao', variant: 'danger', icon: 'ri-shield-star-line' },
  admin: { label: 'Quản trị viên', variant: 'danger', icon: 'ri-admin-line' },
  director: { label: 'Giám đốc', variant: 'purple', icon: 'ri-user-star-line' },
  pm: { label: 'Quản lý dự án', variant: 'info', icon: 'ri-briefcase-line' },
  qa_qc: { label: 'QA/QC', variant: 'orange', icon: 'ri-check-double-line' },
  qs: { label: 'QS', variant: 'orange', icon: 'ri-line-chart-line' },
  site_manager: { label: 'Chỉ huy trưởng', variant: 'info', icon: 'ri-government-line' },
  warehouse: { label: 'Thủ kho', variant: 'secondary', icon: 'ri-database-2-line' },
  accountant: { label: 'Kế toán', variant: 'pink', icon: 'ri-money-dollar-circle-line' },
  secretary: { label: 'Thư ký dự án', variant: 'pink', icon: 'ri-file-user-line' },
  supervisor: { label: 'Giám sát', variant: 'info', icon: 'ri-eye-line' },
  staff: { label: 'Nhân viên', variant: 'default', icon: 'ri-user-3-line' },
  site_foreman: { label: 'Tổ trưởng', variant: 'warning', icon: 'ri-team-line' },
  equipment_manager: { label: 'Quản lý thiết bị', variant: 'info', icon: 'ri-tools-line' },
  zone_manager: { label: 'Quản lý Khu vực', variant: 'purple', icon: 'ri-map-pin-user-line' },
  agro_manager: { label: 'Quản lý Nông hóa phẩm', variant: 'success', icon: 'ri-leaf-line' },
};

/** Returns the configuration for a role code */
export const getRoleConfig = (roleCode: string): RoleConfig => {
  const r = roleCode?.toLowerCase() || '';
  return roleConfigMap[r] || { label: roleCode, variant: 'default', icon: 'ri-user-line' };
};

/** Returns the Badge variant color for a role (Legacy support) */
export const getRoleBadgeVariant = (role: string): any => {
  return getRoleConfig(role).variant;
};

/** Returns the Remix Icon class for a role (Legacy support) */
export const getRoleIcon = (role: string): string => {
  return getRoleConfig(role).icon;
};

/** Returns the display label for a role code (Legacy support) */
export const getRoleLabel = (roleCode: string): string => {
  return getRoleConfig(roleCode).label;
};

/** Returns the CSS classes for a role badge based on its variant */
export const getRoleBadgeClasses = (roleCode: string): string => {
  const variant = getRoleBadgeVariant(roleCode);
  const colors: Record<string, string> = {
    default: 'bg-slate-50 text-slate-600 border-slate-100',
    primary: 'bg-blue-50 text-blue-600 border-blue-100',
    success: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    warning: 'bg-amber-50 text-amber-600 border-amber-100',
    danger: 'bg-rose-50 text-rose-600 border-rose-100',
    info: 'bg-sky-50 text-sky-600 border-sky-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
    orange: 'bg-orange-50 text-orange-600 border-orange-100',
    pink: 'bg-pink-50 text-pink-600 border-pink-100',
    secondary: 'bg-slate-100 text-slate-600 border-slate-200'
  };

  return cn(
    'inline-flex items-center rounded-md border px-2 py-0.5 sm:text-sm text-xs font-normal',
    colors[variant] || colors.default
  );
};
